import { type Permission } from '@ibe/constants';
import { DataTable, Pagination, SearchInput, type DataTableSort } from '@ibe/ui';
import { type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ZodType } from 'zod';

import { NotPermitted, PermissionGuard } from '../../auth/permission-guard.js';
import { errorTextFor } from '../../lib/api/error-message.js';
import { useApiList } from '../../lib/api/hooks.js';
import type { queryKeys } from '../../lib/api/query-keys.js';
import {
  type ColumnDescriptor,
  fieldsForColumns,
  resolveVisibleColumns,
} from '../../lib/resource-list/columns.js';
import { type FilterDescriptor, filterParamsFor } from '../../lib/resource-list/filters.js';
import { useAppStore } from '../../lib/state/store.js';
import { ColumnPicker } from './column-picker.js';
import { FilterBar } from './filter-bar.js';

/**
 * Every list screen in the portal.
 *
 * Fifteen resources are the same page with different columns: search, sort,
 * paginate, choose columns, render rows, handle the three non-data states.
 * What varies is a descriptor; what does not varies nowhere.
 *
 * **View state lives in the URL, client preferences live in the store.** The
 * split is deliberate. A filtered, sorted, paginated view is something people
 * paste to each other and expect the back button to return to. Which columns
 * a reader likes is theirs, follows them between screens, and would be noise
 * in a shared link.
 */

const DEFAULT_PAGE_SIZE = 10;
const FIRST_PAGE = 1;

/**
 * The sort a list opens on when its module names none.
 *
 * Newest first, matching the service: an operator who has just created a record
 * finds it at the top rather than on page four of an alphabetical ordering.
 * A module that genuinely reads better another way overrides it, and says why.
 */
const DEFAULT_SORT: DataTableSort = { columnId: 'createdAt', direction: 'desc' };

export interface ResourceListPageProps<TSchema extends ZodType> {
  readonly title: string;
  readonly description?: string;
  /** Gateway path, e.g. `/v1/properties`. */
  readonly path: string;
  /** Cache root. Must be a key in `queryKeys`. */
  readonly resource: keyof typeof queryKeys;
  /** Schema for one row. Types flow from here. */
  readonly schema: TSchema;
  readonly columns: readonly ColumnDescriptor<TSchema['_output']>[];
  readonly rowKey: (row: TSchema['_output']) => string;
  readonly permission: Permission;
  /**
   * Parameters fixed by the page rather than the reader.
   *
   * A room list is always scoped to one property, and that is not something
   * the search box can clear. Folded into the cache key, because two
   * properties' room lists are two different lists.
   */
  /**
   * Value filters offered above the table.
   *
   * A module declares them; the list machinery round-trips them through the
   * URL and folds them into the request and the cache key.
   */
  readonly filters?: readonly FilterDescriptor[];
  readonly scope?: Readonly<Record<string, string>>;
  readonly defaultSort?: DataTableSort;
  readonly searchLabel?: string;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly actions?: ReactNode;
  readonly pageSize?: number;
  /** A tab or navigation bar shown below the page header. */
  readonly tabs?: ReactNode;
  /**
   * Extra controls placed at the start of the search/filter row.
   *
   * Useful for scoping controls such as a tenant picker that are not filters
   * but should sit alongside the search and filter fields.
   */
  readonly filterPrefix?: ReactNode;
}

export function ResourceListPage<TSchema extends ZodType>({
  title,
  description,
  path,
  resource,
  schema,
  columns,
  rowKey,
  permission,
  filters = [],
  scope,
  defaultSort = DEFAULT_SORT,
  searchLabel,
  emptyTitle,
  emptyDescription,
  actions,
  tabs,
  pageSize = DEFAULT_PAGE_SIZE,
  filterPrefix,
}: ResourceListPageProps<TSchema>): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const selection = useAppStore((state) => state.prefs.columns[resource]);

  const page = Number(params.get('page') ?? String(FIRST_PAGE));
  const search = params.get('q') ?? '';
  const sortBy = params.get('sortBy') ?? defaultSort.columnId;
  const sortDirection = (params.get('sortDirection') ??
    defaultSort.direction) as DataTableSort['direction'];

  const visibleColumns = resolveVisibleColumns(columns, selection);

  const query = useApiList({
    resource,
    path,
    schema,
    params: {
      page,
      pageSize,
      ...(search === '' ? {} : { q: search }),
      sortBy,
      sortDirection,
      // Filters and page-fixed scope both become request parameters, and both
      // belong in the cache key: two filter settings are two different lists.
      scope: { ...(scope ?? {}), ...filterParamsFor(filters, params) },
      // Computed and keyed even though the Gateway ignores it today, so that
      // enabling projection changes a server route and not this component.
      fields: fieldsForColumns(visibleColumns),
    },
  });

  const update = (changes: Readonly<Record<string, string | undefined>>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const sort: DataTableSort = { columnId: sortBy, direction: sortDirection };

  return (
    <PermissionGuard permission={permission} fallback={<NotPermitted />}>
      <section className="space-y-section">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-page-title font-semibold">{title}</h1>
            {description === undefined ? null : (
              <p className="text-body text-muted-foreground">{description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ColumnPicker resource={resource} columns={columns} />
            {actions}
          </div>
        </header>

        {tabs}

        <div className="flex flex-wrap items-end gap-4">
          {filterPrefix}

          <SearchInput
            className="w-full max-w-sm"
            label={searchLabel ?? `Search ${title.toLowerCase()}`}
            defaultValue={search}
            onSearch={(term) => {
              update({ q: term, page: undefined });
            }}
          />

          <FilterBar
            filters={filters}
            params={params}
            onChange={(param, value) => {
              // Back to page one, for the same reason a search does: page seven
              // of the unfiltered set is almost always empty once filtered.
              update({ [param]: value, page: undefined });
            }}
          />
        </div>

        <DataTable
          caption={title}
          columns={visibleColumns}
          rows={query.data?.items ?? []}
          rowKey={rowKey}
          loading={query.isPending}
          error={errorTextFor(query.error)}
          onRetry={() => {
            void query.refetch();
          }}
          sort={sort}
          onSortChange={(next) => {
            update({ sortBy: next.columnId, sortDirection: next.direction, page: undefined });
          }}
          emptyTitle={emptyTitle ?? `No ${title.toLowerCase()} found`}
          {...(emptyDescription === undefined ? {} : { emptyDescription })}
        />

        {query.data === undefined ? null : (
          <Pagination
            meta={query.data.pagination}
            disabled={query.isFetching}
            onPageChange={(next) => {
              update({ page: String(next) });
            }}
          />
        )}
      </section>
    </PermissionGuard>
  );
}
